// Period options of the Variant History tab (FIG-239 · UC-256, FIG-430).
//
// Every period is a range of PT calendar days ('YYYY-MM-DD', both ends inclusive) and never
// reaches into today: a snapshot only exists once its PT day has ended, so the latest day of any
// period is yesterday in PT. Shared by the tab (labels, default) and by variant-history-data-v1
// (the range math), so the two cannot drift. Dependency-free apart from ptDates, which is too.
import { addDays, ptToday, ptYesterday, trailing12Window } from './ptDates.ts';

export const VARIANT_HISTORY_PERIODS = [
  'last_7_days',
  'last_30_days',
  'last_90_days',
  'month_to_date',
  'last_month',
  'quarter_to_date',
  'year_to_date',
  'last_12_months',
] as const;

export type VariantHistoryPeriod = (typeof VARIANT_HISTORY_PERIODS)[number];

export const DEFAULT_VARIANT_HISTORY_PERIOD: VariantHistoryPeriod = 'last_30_days';

export const VARIANT_HISTORY_PERIOD_LABELS: Record<VariantHistoryPeriod, string> = {
  last_7_days: 'Last 7 days',
  last_30_days: 'Last 30 days',
  last_90_days: 'Last 90 days',
  month_to_date: 'Month to date',
  last_month: 'Last month',
  quarter_to_date: 'Quarter to date',
  year_to_date: 'Year to date',
  last_12_months: 'Last 12 months',
};

/** Rows per page of the Variant History table. */
export const VARIANT_HISTORY_PAGE_SIZE = 50;

export interface VariantHistoryRange {
  /** First day (inclusive). */
  from: string;
  /** Last day (inclusive): always yesterday in PT or earlier. */
  to: string;
}

export function isVariantHistoryPeriod(value: unknown): value is VariantHistoryPeriod {
  return typeof value === 'string' && (VARIANT_HISTORY_PERIODS as readonly string[]).includes(value);
}

/**
 * The PT days a period covers, as of `now`. `from` is later than `to` when the period has no day yet
 * (Month to date on the 1st of a month, Quarter to date on the 1st of a quarter, Year to date on 1 Jan):
 * use {@link daysNewestFirst}, which returns no days for such a range.
 */
export function variantHistoryRange(period: VariantHistoryPeriod, now: Date = new Date()): VariantHistoryRange {
  const today = ptToday(now);
  const yesterday = ptYesterday(now);
  const [year, month] = today.split('-').map(Number);
  const pad = (n: number) => String(n).padStart(2, '0');
  const firstOfThisMonth = `${year}-${pad(month)}-01`;

  switch (period) {
    case 'last_7_days':
      return { from: addDays(yesterday, -6), to: yesterday };
    case 'last_30_days':
      return { from: addDays(yesterday, -29), to: yesterday };
    case 'last_90_days':
      return { from: addDays(yesterday, -89), to: yesterday };
    case 'month_to_date':
      return { from: firstOfThisMonth, to: yesterday };
    case 'last_month': {
      const prevMonthYear = month === 1 ? year - 1 : year;
      const prevMonth = month === 1 ? 12 : month - 1;
      return { from: `${prevMonthYear}-${pad(prevMonth)}-01`, to: addDays(firstOfThisMonth, -1) };
    }
    case 'quarter_to_date': {
      const quarterStartMonth = Math.floor((month - 1) / 3) * 3 + 1;
      return { from: `${year}-${pad(quarterStartMonth)}-01`, to: yesterday };
    }
    case 'year_to_date':
      return { from: `${year}-01-01`, to: yesterday };
    case 'last_12_months': {
      // The same 12-month window every EVE figure uses (FIG-429): the 12 months ending yesterday.
      const window = trailing12Window(yesterday);
      return { from: window.startDate, to: window.asOf };
    }
  }
}

/** The days of an inclusive range, newest first; none when `from` is later than `to`. */
export function daysNewestFirst(range: VariantHistoryRange): string[] {
  const days: string[] = [];
  for (let day = range.to; day >= range.from; day = addDays(day, -1)) days.push(day);
  return days;
}
